/**
 * DiagnosticHelper - Tools to help debug orientation and alignment issues
 */

import * as THREE from 'three';

export class DiagnosticHelper {
  constructor(scene) {
    this.scene = scene;
    this.helpers = [];
  }

  /**
   * Add coordinate axes to the scene
   * Red = X axis, Green = Y axis, Blue = Z axis
   */
  addAxesHelper(size = 2) {
    const axesHelper = new THREE.AxesHelper(size);
    axesHelper.name = 'diagnosticAxes';
    this.scene.add(axesHelper);
    this.helpers.push(axesHelper);
    console.log('✅ Axes Helper added (Red=X, Green=Y, Blue=Z)');
  }

  /**
   * Add a grid helper at Y=0 (floor level)
   */
  addGridHelper(size = 5, divisions = 20) {
    const gridHelper = new THREE.GridHelper(size, divisions);
    gridHelper.name = 'diagnosticGrid';
    this.scene.add(gridHelper);
    this.helpers.push(gridHelper);
    console.log('✅ Grid Helper added at Y=0');
  }

  /**
   * Show camera position as a sphere
   */
  showCameraPosition(camera) {
    // Remove old camera indicator
    const oldIndicator = this.scene.getObjectByName('cameraIndicator');
    if (oldIndicator) this.scene.remove(oldIndicator);

    const geometry = new THREE.SphereGeometry(0.1, 16, 16);
    const material = new THREE.MeshBasicMaterial({ color: 0xff00ff });
    const sphere = new THREE.Mesh(geometry, material);
    sphere.name = 'cameraIndicator';
    sphere.position.copy(camera.position);
    this.scene.add(sphere);

    console.log('📍 Camera position:', {
      x: camera.position.x.toFixed(3),
      y: camera.position.y.toFixed(3),
      z: camera.position.z.toFixed(3)
    });
  }

  /**
   * Log panorama position with visual marker
   */
  showPanoramaPosition(panoId, position, color = 0x00ff00, modelOffset = null) {
    const geometry = new THREE.SphereGeometry(0.15, 16, 16);
    const material = new THREE.MeshBasicMaterial({ 
      color: color,
      transparent: true,
      opacity: 0.7
    });
    const sphere = new THREE.Mesh(geometry, material);
    sphere.name = `diagnosticPano_${panoId}`;
    
    // Apply model offset if provided (to account for ModelLoader repositioning)
    const finalX = position.x + (modelOffset?.x || 0);
    const finalY = position.y + (modelOffset?.y || 0);
    const finalZ = position.z + (modelOffset?.z || 0);
    
    sphere.position.set(finalX, finalY, finalZ);
    
    // Add label
    const canvas = document.createElement('canvas');
    canvas.width = 128;
    canvas.height = 64;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = 'white';
    ctx.font = 'bold 40px Arial';
    ctx.textAlign = 'center';
    ctx.fillText(panoId, 64, 45);
    
    const texture = new THREE.CanvasTexture(canvas);
    const spriteMaterial = new THREE.SpriteMaterial({ map: texture });
    const sprite = new THREE.Sprite(spriteMaterial);
    sprite.scale.set(0.3, 0.15, 1);
    sprite.position.y = 0.3;
    sphere.add(sprite);
    
    this.scene.add(sphere);
    this.helpers.push(sphere);

    if (modelOffset && (modelOffset.x !== 0 || modelOffset.y !== 0 || modelOffset.z !== 0)) {
      console.log(`📍 Panorama ${panoId}: Config (${position.x.toFixed(2)}, ${position.y.toFixed(2)}, ${position.z.toFixed(2)}) + Model offset (${modelOffset.x.toFixed(2)}, ${modelOffset.y.toFixed(2)}, ${modelOffset.z.toFixed(2)}) = Final (${finalX.toFixed(2)}, ${finalY.toFixed(2)}, ${finalZ.toFixed(2)})`);
    } else {
      console.log(`📍 Panorama ${panoId} at: (${finalX.toFixed(3)}, ${finalY.toFixed(3)}, ${finalZ.toFixed(3)})`);
    }
  }

  /**
   * Compare model bounds with panorama positions
   */
  checkAlignment(modelBounds, panoramaPositions) {
    console.group('🔍 Alignment Check');
    
    console.log('Model bounds:', modelBounds);
    
    const positions = Object.values(panoramaPositions);
    const minX = Math.min(...positions.map(p => p.position.x));
    const maxX = Math.max(...positions.map(p => p.position.x));
    const minZ = Math.min(...positions.map(p => p.position.z));
    const maxZ = Math.max(...positions.map(p => p.position.z));
    
    console.log('Panorama bounds:', {
      x: { min: minX, max: maxX },
      z: { min: minZ, max: maxZ }
    });
    
    // Check if panoramas are inside model bounds
    const modelX = { min: modelBounds.x.min, max: modelBounds.x.max };
    const modelZ = { min: modelBounds.z.min, max: modelBounds.z.max };
    
    if (minX < modelX.min || maxX > modelX.max || minZ < modelZ.min || maxZ > modelZ.max) {
      console.warn('⚠️ Some panoramas are OUTSIDE model bounds!');
      console.log('This might indicate:');
      console.log('  1. Placeholder positions need adjustment');
      console.log('  2. Model needs rotation/scaling');
      console.log('  3. Coordinate system mismatch');
    } else {
      console.log('✅ All panoramas within model bounds');
    }
    
    console.groupEnd();
  }

  /**
   * Test raycasting from a position
   */
  testRaycast(position, model) {
    console.group('🎯 Raycast Test from position', position);
    
    const raycaster = new THREE.Raycaster();
    const directions = {
      forward: new THREE.Vector3(0, 0, -1),
      back: new THREE.Vector3(0, 0, 1),
      left: new THREE.Vector3(-1, 0, 0),
      right: new THREE.Vector3(1, 0, 0),
      down: new THREE.Vector3(0, -1, 0)
    };
    
    Object.entries(directions).forEach(([name, dir]) => {
      raycaster.set(position, dir);
      const intersects = raycaster.intersectObject(model, true);
      
      if (intersects.length > 0) {
        console.log(`  ${name}: Hit at distance ${intersects[0].distance.toFixed(2)}m`);
      } else {
        console.log(`  ${name}: No hit (might be outside model)`);
      }
    });
    
    console.groupEnd();
  }

  /**
   * Remove all diagnostic helpers
   */
  clear() {
    this.helpers.forEach(helper => {
      this.scene.remove(helper);
      if (helper.geometry) helper.geometry.dispose();
      if (helper.material) helper.material.dispose();
    });
    this.helpers = [];
    console.log('🗑️ Diagnostic helpers cleared');
  }

  /**
   * Show quick alignment suggestions
   */
  suggestFixes(modelBounds, panoramaPositions) {
    console.group('💡 Alignment Suggestions');
    
    const modelCenter = {
      x: (modelBounds.x.min + modelBounds.x.max) / 2,
      z: (modelBounds.z.min + modelBounds.z.max) / 2
    };
    
    console.log('Try these quick fixes:');
    console.log('');
    console.log('1. If model is rotated wrong:');
    console.log('   In ModelLoader.js, add: model.rotation.y = Math.PI;');
    console.log('');
    console.log('2. If panoramas are offset:');
    console.log(`   Adjust all positions by: x+${(-modelCenter.x).toFixed(2)}, z+${(-modelCenter.z).toFixed(2)}`);
    console.log('');
    console.log('3. Best solution:');
    console.log('   Use calibration tool: http://localhost:3000/calibration.html');
    console.log('   Manually align each panorama with the model');
    
    console.groupEnd();
  }
}

